import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import PageNotFound from "../pagenotfound/pagenotfound.tsx";
import { EngineeringImage, engineeringProjects } from "./engineeringData.tsx";

function chunkImages(images: EngineeringImage[]) {
  const rows: EngineeringImage[][] = [];
  for (let index = 0; index < images.length; index += 2) {
    rows.push(images.slice(index, index + 2));
  }
  return rows;
}

function ImagePair({ images }: { images: EngineeringImage[] }) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const photos = useRef<(HTMLImageElement | null)[]>([]);
  const [sizes, setSizes] = useState<{ width: number; height: number }[] | null>(null);

  const measure = useCallback(() => {
    const row = rowRef.current;
    const sideBySide = window.matchMedia("(min-width: 640px)").matches;
    if (!sideBySide || images.length < 2 || !row) {
      setSizes(null);
      return;
    }

    const aspects = images.map((_, index) => {
      const photo = photos.current[index];
      if (!photo?.naturalWidth || !photo.naturalHeight) {
        return 0;
      }
      return photo.naturalWidth / photo.naturalHeight;
    });

    if (aspects.some((aspect) => aspect <= 0)) {
      return;
    }

    const gap = parseFloat(window.getComputedStyle(row).columnGap) || 0;
    const available = (row.clientWidth - gap * (images.length - 1)) * 0.75;
    const height = Math.floor(available / aspects.reduce((sum, aspect) => sum + aspect, 0));
    const next = aspects.map((aspect) => ({
      height,
      width: Math.floor(height * aspect),
    }));
    setSizes((current) => {
      if (
        current &&
        current.length === next.length &&
        current.every((size, index) => size.width === next[index].width && size.height === next[index].height)
      ) {
        return current;
      }
      return next;
    });
  }, [images]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  if (images.length < 2) {
    const image = images[0];
    return (
      <figure className="mx-auto flex w-full flex-col sm:w-[37.5%]">
        <img className="h-auto w-full rounded-md shadow-xl" src={image.src} alt={image.alt} />
        <figcaption className="pt-4 text-center text-lg font-medium">{image.caption}</figcaption>
        {image.body && <p className="pt-2 text-left text-base font-light">{image.body}</p>}
      </figure>
    );
  }

  return (
    <div ref={rowRef} className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-center">
      {images.map((image, index) => (
        <figure
          key={image.caption}
          className="flex w-full flex-col"
          style={sizes ? { width: sizes[index].width } : undefined}
        >
          <img
            ref={(node) => {
              photos.current[index] = node;
            }}
            onLoad={measure}
            className="h-auto w-full rounded-md object-contain shadow-xl"
            style={sizes ? { height: sizes[index].height } : undefined}
            src={image.src}
            alt={image.alt}
          />
          <figcaption className="pt-4 text-center text-lg font-medium">{image.caption}</figcaption>
          {image.body && <p className="pt-2 text-left text-base font-light">{image.body}</p>}
        </figure>
      ))}
    </div>
  );
}

function EngineeringProject() {
  const { slug } = useParams();
  const project = engineeringProjects.find((item) => item.slug === slug);

  if (!project) {
    return <PageNotFound />;
  }

  document.title = `Vihas Veggalam | ${project.name}`;

  return (
    <div className="w-11/12 lg:w-4/5 m-auto pb-16">
      <h1 className="text-4xl sm:text-5xl font-semibold pt-4">{project.name}</h1>
      <p className="text-lg sm:text-2xl font-light pt-4 max-w-4xl">{project.summary}</p>
      <div className="relative left-1/2 flex w-screen -translate-x-1/2 flex-col gap-10 px-6 pt-10 sm:px-8">
        {chunkImages(project.images).map((row) => (
          <ImagePair key={row[0].caption} images={row} />
        ))}
      </div>
    </div>
  );
}

export default EngineeringProject;
